<script setup>
/**
 * A treemap, drawn as positioned boxes rather than on a canvas: the tiles are
 * few, their labels are text, and text is what a treemap is read by.
 *
 * The layout is `lib/treemap.js`'s, recomputed whenever the box changes size —
 * a squarified layout depends on the box's proportions, so it cannot simply be
 * stretched. Until the box has been measured (and on the server) there are no
 * tiles, only the legend and the readable table.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { squarify } from '../../lib/treemap'
import { t } from '../../stores/i18n'

const props = defineProps({
  /** `{ key, name, value, share, color }`, `color` naming a CSS variable. */
  tiles: { type: Array, default: () => [] },
  /** `{ label, color }` — what the colours mean. */
  legend: { type: Array, default: () => [] },
  height: { type: Number, default: 132 },
  format: { type: Function, default: (v) => String(v) },
  masked: { type: Boolean, default: false },
  caption: { type: String, default: '' },
})

const root = ref(null)
const box = ref(null)
const width = ref(0)
let observer = null

onMounted(() => {
  const measure = () => (width.value = box.value?.clientWidth ?? 0)
  measure()
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(measure)
    observer.observe(box.value)
  }
})
onBeforeUnmount(() => observer?.disconnect())

const GAP = 2

const laid = computed(() =>
  squarify(props.tiles, width.value, props.height).map((tile) => {
    // Room decides what a tile can say: a name, then its figure beneath.
    const w = Math.max(0, tile.w - GAP)
    const h = Math.max(0, tile.h - GAP)
    return {
      ...tile,
      box: { left: `${tile.x}px`, top: `${tile.y}px`, width: `${w}px`, height: `${h}px` },
      // A sliver narrower than its own padding and border would be widened
      // past its place by the browser; it gets neither.
      tiny: w < 16 || h < 16,
      named: w >= 44 && h >= 22,
      figured: !props.masked && w >= 56 && h >= 40,
    }
  }),
)

const percent = (share) => `${Math.round(share * 100)}%`

/**
 * The hovered tile, said in full: its name, what it moved and its share — the
 * same card the other charts use, following the pointer. It sits outside the
 * tile box, which clips, and turns to below the pointer near the top so it is
 * never cut off by the card. A tap shows it too, for a finger that cannot hover.
 */
const tip = ref(null)
const TIP_HALF = 80
function point(tile, event) {
  const frame = root.value?.getBoundingClientRect()
  const area = box.value?.getBoundingClientRect()
  if (!frame || !area) return
  const x = event.clientX - frame.left
  const y = event.clientY - frame.top
  tip.value = {
    tile,
    x: Math.min(Math.max(x, TIP_HALF), Math.max(TIP_HALF, frame.width - TIP_HALF)),
    y,
    below: event.clientY - area.top < 56,
  }
}
const leave = () => (tip.value = null)
</script>

<template>
  <!-- `relative` for the same reason as Chart.vue's: the readable table is
       `sr-only`, and must not escape to the page and push it sideways. -->
  <div ref="root" class="relative" @pointerleave="leave">
    <ul v-if="legend.length" class="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-muted">
      <li v-for="item in legend" :key="item.label" class="flex items-center gap-1.5">
        <span class="size-2 rounded-[2px]" :style="{ background: `var(${item.color})` }" />
        {{ item.label }}
      </li>
    </ul>

    <div
      ref="box"
      class="relative w-full overflow-hidden"
      :style="{ height: `${height}px` }"
      aria-hidden="true"
      data-treemap
    >
      <div
        v-for="tile in laid"
        :key="tile.key"
        class="absolute flex flex-col justify-start overflow-hidden leading-tight"
        :class="tile.tiny ? 'rounded-[2px]' : 'rounded-md border px-1.5 py-1'"
        :style="{
          ...tile.box,
          background: `color-mix(in srgb, var(${tile.color}) 26%, var(--c-surface))`,
          borderColor: `color-mix(in srgb, var(${tile.color}) 55%, transparent)`,
        }"
        :data-tile="tile.key"
        @pointermove="point(tile, $event)"
        @pointerdown="point(tile, $event)"
      >
        <span v-if="tile.named" class="truncate text-[11.5px] font-semibold text-text">{{ tile.name }}</span>
        <span v-if="tile.figured" class="tnum truncate text-[11px] text-muted">
          {{ format(tile.value) }} · {{ percent(tile.share) }}
        </span>
      </div>
    </div>

    <div
      v-if="tip"
      class="pointer-events-none absolute z-10 rounded-lg border border-line bg-surface px-2 py-1
             shadow-[var(--c-shadow)]"
      :style="{
        left: `${tip.x}px`,
        top: `${tip.y}px`,
        transform: `translate(-50%, ${tip.below ? '14px' : 'calc(-100% - 10px)'})`,
      }"
      data-treemap-tip
    >
      <p class="flex items-center gap-1.5 text-[12.5px] font-semibold whitespace-nowrap">
        <span class="size-2 shrink-0 rounded-[2px]" :style="{ background: `var(${tip.tile.color})` }" />
        {{ tip.tile.name }}
      </p>
      <!-- Privacy hides the figures, never the name. -->
      <p v-if="!masked" class="tnum mt-0.5 text-[12px] whitespace-nowrap text-muted">
        <span class="font-semibold text-text">{{ format(tip.tile.value) }}</span>
        · {{ percent(tip.tile.share) }}
      </p>
    </div>

    <p v-if="masked" class="sr-only">{{ t('charts.maskedNote', { caption }) }}</p>
    <div v-else class="sr-only">
      <table>
        <caption>{{ caption }}</caption>
        <tbody>
          <tr v-for="tile in tiles" :key="tile.key">
            <th scope="row">{{ tile.name }}</th>
            <td>{{ format(tile.value) }}</td>
            <td>{{ percent(tile.share) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
